import {Snackbar } from '@mui/material';
import {Alert} from '@mui/material';
import * as Sentry from "@sentry/react";
import WidgetEditModal from "modules/widget/widgetEditModal";
import { useFirebase } from "containers/FirebaseContext";
import { collection, onSnapshot, query, where } from "firebase/firestore";
import { filter, find, get, has, isEqual, shuffle } from "lodash";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useDispatch } from "react-redux";
import { useUsersByClass } from "store/userClasses/selectors";
import { getClassUsers } from "store/userClasses/thunks";
import { useCurrentUser } from "store/users/selectors";
import { revokeTrafficModal } from "store/wheel/actions";
import { useTraffic } from "store/wheel/selectors";
import { useOneWayWidgets } from "store/widgets/selectors";
import { handleCreateWidgets } from "store/widgets/thunks";
import { updateStation } from "store/widgetWheel/actions";
import { useWidgetStation } from "store/widgetWheel/selectors";
import { useUserWidgetsConfig } from "utils/methods/firebaseUserWidgets";
import { useCollectionWidgets } from "utils/methods/firebaseWidgets";
import { v4 as uuid } from "uuid";
import WidgetStationComponent from "./widgetStationComponent";

const StationWidget = ({ currentClass, sharableWidget }) => {
  const dispatch = useDispatch();
  // ** States :-
  const [openEditModal, setOpenEditModal] = useState(false);
  const [loader, setLoader] = useState({ type: "", value: false });
  const [currentClasId, setCurrentClassId] = useState(currentClass?.id);
  const [toastMessage, setToastMessage] = useState({
    open: false,
    message: "",
  });

  // ** use-ref
  const isInitialMount = useRef(true);

  // ** custom hooks :-
  const stationWidgetData = useWidgetStation();
  const [trafficModal] = useTraffic();
  const usersByClass = useUsersByClass();
  const { firestore } = useFirebase();
  const currentUser = useCurrentUser();
  const { createCollectionWidgets } = useCollectionWidgets();
  const { createUserWidgetsConfig } = useUserWidgetsConfig();
  const dataCheckList = useOneWayWidgets();
  const [fetchAllStudents, setFetchAllStudents] = useState([]);

  const getFetchStudent = async () => {
    try {
      if (!currentClass?.id) return;
      const students = await getClassUsers(currentClass?.id);
      const filterStudent =
        filter(students, (x) => x.role === "student" && x.active &&x?.userclass?.active) || [];
      setFetchAllStudents(filterStudent);
    } catch (error) {
      Sentry.captureException(error);
    }
  };

  // ** memo declaration :-
  const stationWidget = useMemo(() => stationWidgetData, [stationWidgetData]);

  const trafficData = useMemo(
    () => trafficModal?.widget_menu ?? [],
    [trafficModal]
  );

  const filterByWidget = useMemo(
    () => find(trafficData, (item) => item?.menu === "new_station"),
    [trafficData]
  );

  const filterShareStation = useMemo(
    () => find(sharableWidget, (obj) => obj?.widget_type === "new_station"),
    [sharableWidget]
  );

  const sharedData = useMemo(
    () => filterShareStation?.data,
    [filterShareStation]
  );

  const widgetStationOpen = useMemo(
    () =>
      get(currentUser, "role") === "teacher"
        ? filterByWidget?.view && filterByWidget?.menu === "new_station"
        : filterShareStation?.id,
    [filterByWidget, filterShareStation, currentUser]
  );

  const checkStudentPerGroup = useMemo(() => {
    const checkStudentGroup =
      !stationWidget?.studentPerGroup ||
      stationWidget?.studentPerGroup === "" ||
      stationWidget?.studentPerGroup === 0 ||
      stationWidget?.studentPerGroup === "0";

    return checkStudentGroup;
  }, [stationWidget?.studentPerGroup]);

  const getStationData = useMemo(
    () =>
      dataCheckList?.find(
        (element) =>
          element?.type === "new_station" &&
          element.class_id?.includes(currentClass?.id)
      ) || {},
    [dataCheckList, currentClass?.id]
  );
  const stationData = useMemo(() => {
    if (Object.entries(getStationData)?.length > 0) {
      return getStationData?.data;
    } else {
      return stationWidget?.stations;
    }
  }, [getStationData, dataCheckList, currentClass?.id]);

  useEffect(() => {
    getFetchStudent();
  }, [currentClass?.id, widgetStationOpen]);

  const students = useMemo(() => fetchAllStudents || [], [fetchAllStudents,currentClass?.id]);

  // ** Functions & Effect's:-
  const updateState = async (data) => {
    await dispatch(updateStation(data));
  };

  useEffect(() => {
    if (widgetStationOpen) {
      document.body.classList.add("body-overflow-hidden");
    }

    return () => {
      document.body.classList.remove("body-overflow-hidden");
    };
  }, [widgetStationOpen]);

  useEffect(() => {
    const getCheck = async () => {
      if (
        (filterShareStation && !filterShareStation?.status) ||
        filterShareStation === undefined
      ) {
        await updateState({
          generatedGroup: [],
          studentPerGroup: 0,
        });
      }
    };
    getCheck();
  }, [widgetStationOpen]);

  useEffect(() => {
    const getSharedData = async () => {
      if (
        filterShareStation?.id &&
        filterShareStation?.status &&
        filterShareStation?.data
      ) {
        await updateState({
          generatedGroup: sharedData?.generatedGroup || [],
          studentPerGroup: sharedData?.studentPerGroup || 0,
        });
        return;
      }
    };
    getSharedData();
  }, [
    currentClass.id,
    currentUser,
    usersByClass[currentClass.id],
    sharableWidget,
  ]);

  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }

    if (
      filterShareStation &&
      filterShareStation?.status &&
      !isEqual(sharedData?.generatedGroup, stationWidget?.generatedGroup)
    ) {
      const data = {
        ...filterShareStation,
        data: {
          generatedGroup: stationWidget?.generatedGroup || [],
          studentPerGroup: stationWidget?.studentPerGroup || 0,
        },
      };
      createCollectionWidgets(data);
    }
  }, [stationWidget]);

  const getUsers = useCallback(() => {
    if (students && has(currentClass, "id")) {
      const user = students?.map((data) => {
        return {
          userName: `${data.firstName || ""} ${data.lastName || ""}`,
          id: data?.id,
          role: data?.role,
        };
      });

      return user || [];
    }
    return [];
  }, [currentClass, usersByClass, students]);

  useEffect(() => {
    let unsubscribe = () => {};
    listenToWidgetUserConfig(get(currentClass, "id", "")).then(
      (unsubscribeFunc) => {
        unsubscribe = unsubscribeFunc;
      }
    );
    return () => {
      unsubscribe();
    };
  }, [
    firestore,
    currentClass.id,
    currentUser,
    students,
    usersByClass[currentClass.id],
    filterByWidget,
  ]);

  const listenToWidgetUserConfig = async (classID) => {
    if (!classID || !currentUser || filterByWidget?.menu !== "new_station") {
      return () => {};
    }
    const fifteenHoursAgo = new Date();
    fifteenHoursAgo.setHours(fifteenHoursAgo.getHours() - 12);

    const collectionRef = collection(
      firestore,
      `widgets_user_config/${classID}/widgets`
    );
    const q = query(
      collectionRef,
      where("id", "==", "new_station"),
      where("modified_on", ">=", fifteenHoursAgo)
    );

    const unsubscribe = onSnapshot(
      q,
      async (querySnapshot) => {
        let sharableData = null;
        querySnapshot.forEach((doc) => {
          sharableData = { id: doc.id, ...doc.data() };
        });
        if (
          !!sharableData &&
          sharableData?.user_data?.length !== 0 &&
          sharableData?.selectedUser?.length !== 0
        ) {
          const user = await getUsers();

          if (user?.length > 0) {
            const allUserId = user?.map((ele) => ele.id) || [];
            const filteredUser =
              sharableData?.user_data?.filter(
                (ele) => !allUserId?.includes(ele.id)
              ) || [];
            const payload = [...user, ...filteredUser];
            await dispatch(
              updateStation({
                users: payload || [],
                selectedUser: sharableData?.selectedUser,
              })
            );
          } else {
            await dispatch(
              updateStation({
                users: sharableData?.user_data,
                selectedUser: sharableData?.selectedUser || [],
              })
            );
          }
        } else {
          const user = await getUsers();
          await dispatch(
            updateStation({ users: shuffle(user), selectedUser: user })
          );
        }
      },
      (error) => {
        console.error("Error listening to widgets user config changes:", error);
      }
    );

    return unsubscribe;
  };

  // ** Function Handler
  const handleOpenEditModal = (value) => {
    setOpenEditModal(value);
  };

  const handlerGenerateStudentGroup = async (selectedUser) => {
    if (checkStudentPerGroup) {
      setToastMessage({
        open: true,
        message: "Students per station field cannot be empty.",
      });
      return;
    }
    const users = selectedUser || stationWidget?.selectedUser;

    const shuffledStudents = shuffle([...users]);
    const generatedGroup = stationData
      ?.filter((ele) => ele?.enabled)
      ?.map((station, index, array) => {
        const start = index * stationWidget?.studentPerGroup;

        const students =
          index === array.filter((ele) => ele?.enabled).length - 1
            ? shuffledStudents.slice(start)
            : shuffledStudents.slice(
                start,
                start + parseInt(stationWidget?.studentPerGroup)
              );

        return {
          ...station,
          students: students,
        };
      });

    await dispatch(updateStation({ generatedGroup }));
  };

  const handleUserNewAdded = async (newGeneratedGroup, newUser) => {
    if (
      newUser?.firstName?.trim()?.length === 0 ||
      newUser?.lastName?.trim()?.length === 0
    ) {
      setToastMessage({
        open: true,
        message: "Either a first name or a last name is required.",
      });
      return;
    }
    try {
      const userPayload = {
        id: uuid(),
        role: "student",
        userName: `${newUser?.firstName || ""} ${newUser?.lastName || ""}`,
      };
      const users = [...stationWidget?.users, userPayload];
      const selectedUser = [...stationWidget?.selectedUser, userPayload];
      await updateState({
        users,
        selectedUser,
        generatedGroup: newGeneratedGroup,
      });
    } catch (error) {
      Sentry.captureException(`375 Error during handleUserNewAdded: ${error}`);
      console.log(error);
    }
  };

  const handlerDone = async (users, selectedUser, action) => {
    setLoader({ type: "save-loader", value: true });
    try {
      const WidgetsData = {
        id: "new_station",
        modified_on: new Date(),
        user_data: users,
        selectedUser: selectedUser,
      };
      await createUserWidgetsConfig(WidgetsData, currentClass?.id);
      if (!checkStudentPerGroup) {
        await handlerGenerateStudentGroup(selectedUser);
      }
      if (action === "custom-done") {
        return;
      }
      handleOpenEditModal(false);
    } catch (error) {
      Sentry.captureException(`376 Error during handlerDone: ${error}`);
      console.log(error);
    } finally {
      setLoader({ type: "", value: false });
    }
  };

  const resetAllList = async () => {
    setLoader({ type: "reset-list", value: true });
    try {
      const WidgetsData = {
        id: "new_station",
        modified_on: new Date(),
        user_data: [],
        selectedUser: [],
      };
      const users = await getUsers();
      await createUserWidgetsConfig(WidgetsData, currentClass?.id);
      await updateState({ users: shuffle(users), selectedUser: users });
      if (stationWidget?.generatedGroup?.length > 0) {
        handlerGenerateStudentGroup(users);
      }
      handleOpenEditModal(false);
    } catch (error) {
      Sentry.captureException(`377 Error during resetAllList: ${error}`);
      console.log(error);
    } finally {
      setLoader({ type: "", value: false });
    }
  };

  const handlerShareGroup = () => {
    const isGenerated = stationWidget?.generatedGroup?.length > 0;
    if (!filterShareStation?.status && (checkStudentPerGroup || !isGenerated)) {
      setToastMessage({
        open: true,
        message: "The station has not been generated yet.",
      });
      return;
    }
    if (!filterShareStation?.id && isGenerated && !filterShareStation?.status) {
      shareGroup();
      return;
    } else {
      unshareGroup();
    }
  };

  const shareGroup = () => {
    const shareData = {
      id: uuid(),
      class_id: currentClass?.id,
      created_by: currentUser?.id,
      created_at: new Date(),
      widget_type: "new_station",
      status: true,
      message: "",
      data: {
        generatedGroup: stationWidget?.generatedGroup,
        studentPerGroup: stationWidget?.studentPerGroup,
      },
    };
    createCollectionWidgets(shareData);
  };

  const unshareGroup = () => {
    const data = {
      ...filterShareStation,
      status: false,
      data: {},
      message: "station",
    };
    createCollectionWidgets(data);
  };

  const onClose = async () => {
    if(filterShareStation?.status){
      await unshareGroup();
    }
    await dispatch(revokeTrafficModal(filterByWidget?.id));
    if (
      (filterShareStation && !filterShareStation?.status) ||
      filterShareStation === undefined ||
      !filterShareStation
    ) {
      await updateState({
        generatedGroup: [],
        studentPerGroup: 0,
      });
    }
  };

  const resetGroup = async () => {
    if (stationWidget?.generatedGroup?.length === 0 && checkStudentPerGroup) {
      setToastMessage({
        open: true,
        message: "The station has not been generated yet.",
      });
      return;
    }
    await updateState({ generatedGroup: [], studentPerGroup: 0 });
  };

  const onChangePerStation = async (value) => {
    await updateState({ studentPerGroup: value });
  };

  const updateGeneratedGroup = async (groupData) => {
    await updateState({ generatedGroup: groupData });
  };

  const handleToogleEnabledOrDisabled = async (id) => {
    const updateStation = stationData.map((element) => {
      if (element?.id === id) {
        return { ...element, enabled: !element?.enabled };
      }
      return element;
    });

    const updateStationPayload = {
      ...(getStationData?.id && { id: getStationData.id }),
      user_id: currentUser?.id,
      type: "new_station",
      class_id: [currentClasId],
      data: updateStation,
    };
    await dispatch(handleCreateWidgets(updateStationPayload));
  };

  const handleDeleteStation = async (id) => {
    const updateStation =
      stationData.filter((element) => element?.id !== id) || [];

    const updateStationPayload = {
      ...(getStationData?.id && { id: getStationData.id }),
      user_id: currentUser?.id,
      type: "new_station",
      class_id: [currentClasId],
      data: updateStation,
    };
    await dispatch(handleCreateWidgets(updateStationPayload));
  };

  const handleStationChange = async (id, newStation) => {
    const updateStation = stationData.map((element) => {
      if (element?.id === id) {
        return { ...element, label: newStation?.label };
      }
      return element;
    });

    const updateStationPayload = {
      ...(getStationData?.id && { id: getStationData.id }),
      user_id: currentUser?.id,
      type: "new_station",
      class_id: [currentClasId],
      data: updateStation,
    };
    await dispatch(handleCreateWidgets(updateStationPayload));
  };

  const handleCreateNewStation = async (newStation) => {
    const payload = {
      ...newStation,
      id: uuid(),
    };
    if (newStation?.label?.length > 0) {
      const updatedStation = [...stationData, payload];

      const updateStationPayload = {
        ...(getStationData?.id && { id: getStationData.id }),
        user_id: currentUser?.id,
        type: "new_station",
        class_id: [currentClasId],
        data: updatedStation,
      };
      await dispatch(handleCreateWidgets(updateStationPayload));
    }
  };

  const handleClose = () => {
    setToastMessage({ open: false, message: "" });
  };

  return (
    <div className={`${widgetStationOpen ? "disable-key-navigation" : ""}`}>
      <Snackbar
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        open={toastMessage.open}
        autoHideDuration={2000}
        onClose={handleClose}
      >
        <Alert onClose={handleClose} severity="error">
          <strong>ALERT:</strong> {toastMessage.message}
        </Alert>
      </Snackbar>
      {widgetStationOpen && (
        <WidgetStationComponent
          states={{
            currentUser,
            shareStatus: filterShareStation?.status,
            perGroupStudent: stationWidget?.studentPerGroup,
            generatedGroup: stationWidget?.generatedGroup || [],
            stations: stationData || [],
          }}
          funcObj={{
            onClose,
            resetGroup,
            onChangePerStation,
            updateGeneratedGroup,
            handlerShareGroup,
            handleOpenEditModal,
            handlerGenerateStudentGroup,
            handleToogleEnabledOrDisabled,
            handleDeleteStation,
            handleUserNewAdded,
            handleStationChange,
            handleCreateNewStation,
          }}
        />
      )}
      <WidgetEditModal
        open={openEditModal}
        close={() => handleOpenEditModal(false)}
        users={stationWidget?.users}
        selectedUser={stationWidget?.selectedUser}
        loader={loader}
        handlerDone={handlerDone}
        resetAllList={resetAllList}
      />
    </div>
  );
};

export default StationWidget;
